import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchStatuses } from '../../../constants/fetchStatuses';
import {
  FetchAccumulatedAumReportResponse,
  FetchAlpacaJournalResponse,
  FetchAumReportResponse,
  FetchCashReportResponse,
  FetchCmaUsersReportResponse,
  FetchFeeCalculationsReportResponse,
  FetchFinanceUsersReportResponse,
  FetchPortfolioCompositionReportResponse,
  FetchPortfolioSnapshotReportResponse,
  FetchOrderFxRevenueReportResponse,
  FetchPortfolioOrderExecutionsReportResponse,
  FetchPortfolioOrdersDataReportResponse,
  FetchSystemCashReportResponse,
  FetchSystemUnitsReportResponse,
  FetchTransferFxRevenueReportResponse,
  FetchUnitReportResponse,
  FetchUserDetailsReportResponse,
  FetchUserIndexFundReportResponse,
} from '../../../types/report';
import {
  cleanReport,
  fetchAlpacaJournalReport,
  fetchCashReport,
  fetchCashTransactionsReport,
  fetchCmaUsersReport,
  fetchFeeCalculationsReport,
  fetchFinanceUsersReport,
  fetchPortfolioCompositionReport,
  fetchPortfolioSnapshotReport,
  fetchFxRevenueAlpacaOrdersReport,
  fetchAccumulatedAumReport,
  fetchAumReport,
  fetchOrdersReport,
  fetchPortfolioOrdersDataReport,
  fetchPortfolioDetailsReport,
  fetchPortfolioTotalsReport,
  fetchUsersReport,
  fetchUserDetailsReport,
  fetchFxRevenueAlpacaTransfersReport,
  fetchFxRevenueJadwaOrdersReport,
  fetchFxRevenueJadwaTransfersReport,
  fetchPortfolioOrderExecutionReport,
  fetchSystemCashReport,
  fetchSystemUnitsReport,
  fetchUnitsReport,
  fetchUserIndexFundReport,
  reconcileSystemCashReport,
} from './report.actions';
import { ReportState } from './report.types';

const initialState: ReportState = {
  portfolioOrderExecutions: [],
  totalPortfolioOrderExecutions: 0,
  fetchPortfolioOrderExecutionsStatus: fetchStatuses.idle,

  cashItems: [],
  totalCashItems: 0,
  fetchCashReportItemsStatus: fetchStatuses.idle,

  cashTransactionsItems: [],
  totalCashTransactionsItems: 0,
  fetchCashTransactionsReportStatus: fetchStatuses.idle,

  usersReportItems: [],
  totalUsersReportItems: 0,
  fetchUsersReportStatus: fetchStatuses.idle,

  userDetailsReportItems: [],
  totalUserDetailsReportItems: 0,
  fetchUserDetailsReportStatus: fetchStatuses.idle,

  feeCalculationsReportItems: [],
  totalFeeCalculationsReportItems: 0,
  fetchFeeCalculationsReportStatus: fetchStatuses.idle,

  financeUsersReportItems: [],
  totalFinanceUsersReportItems: 0,
  fetchFinanceUsersReportStatus: fetchStatuses.idle,

  portfolioCompositionReportItems: [],
  totalPortfolioCompositionReportItems: 0,
  fetchPortfolioCompositionReportStatus: fetchStatuses.idle,

  portfolioSnapshotReportItems: [],
  totalPortfolioSnapshotReportItems: 0,
  fetchPortfolioSnapshotReportStatus: fetchStatuses.idle,

  ordersReportItems: [],
  totalOrdersReportItems: 0,
  fetchOrdersReportStatus: fetchStatuses.idle,

  portfolioOrdersDataReportItems: [],
  totalPortfolioOrdersDataReportItems: 0,
  fetchPortfolioOrdersDataReportStatus: fetchStatuses.idle,

  aumReportItems: [],
  totalAumReportItems: 0,
  fetchAumReportStatus: fetchStatuses.idle,

  accumulatedAumReportItems: [],
  totalAccumulatedAumReportItems: 0,
  fetchAccumulatedAumReportStatus: fetchStatuses.idle,

  portfolioDetailsReportItems: [],
  totalPortfolioDetailsReportItems: 0,
  fetchPortfolioDetailsReportStatus: fetchStatuses.idle,

  portfolioTotalsReportItems: [],
  totalPortfolioTotalsReportItems: 0,
  fetchPortfolioTotalsReportStatus: fetchStatuses.idle,

  systemReport: {},
  fetchSystemCashReportStatus: fetchStatuses.idle,

  unitItems: [],
  totalUnitItems: 0,
  fetchUnitsReportItemsStatus: fetchStatuses.idle,

  systemUnitsReport: [],
  fetchSystemUnitsReportStatus: fetchStatuses.idle,
  reconcileSystemCashReportStatus: fetchStatuses.idle,

  alpacaFxRevenueOrders: [],
  totalAlpacaFxRevenueOrders: 0,
  fetchAlpacaFxRevenueOrdersStatus: fetchStatuses.idle,

  alpacaFxRevenueTransfers: [],
  totalAlpacaFxRevenueTransfers: 0,
  fetchAlpacaFxRevenueTransferStatus: fetchStatuses.idle,

  jadwaFxRevenueOrders: [],
  totalJadwaFxRevenueOrders: 0,
  fetchJadwaFxRevenueOrdersStatus: fetchStatuses.idle,

  jadwaFxRevenueTransfers: [],
  totalJadwaFxRevenueTransfers: 0,
  fetchJadwaFxRevenueTransferStatus: fetchStatuses.idle,

  userIndexFundReport: [],
  totalUserIndexFundReport: 0,
  fetchUserIndexFundReportStatus: fetchStatuses.idle,

  alpacaJournalReport: [],
  totalAlpacaJournalReport: 0,
  fetchAlpacaJournalReportStatus: fetchStatuses.idle,

  cmaUsersItems: [],
  totalCmaUsersItems: 0,
  fetchCmaUsersReportStatus: fetchStatuses.idle,

  error: null,
};

const portfolioOrdersSlice = createSlice({
  name: 'portfolioOrders',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchPortfolioOrderExecutionReport.pending, (state) => {
      state.error = null;
      state.fetchPortfolioOrderExecutionsStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchPortfolioOrderExecutionReport.fulfilled,
      (state, action: PayloadAction<FetchPortfolioOrderExecutionsReportResponse>) => {
        state.portfolioOrderExecutions = action.payload.data.items;
        state.totalPortfolioOrderExecutions = action.payload.data.totalItems;
        state.fetchPortfolioOrderExecutionsStatus = fetchStatuses.success;
      },
    );
    builder.addCase(fetchPortfolioOrderExecutionReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;

      state.fetchPortfolioOrderExecutionsStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchCashReport.pending, (state) => {
      state.error = null;
      state.fetchCashReportItemsStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchCashReport.fulfilled, (state, action: PayloadAction<FetchCashReportResponse>) => {
      state.cashItems = action.payload.data.items;
      state.totalCashItems = action.payload.data.totalItems;
      state.fetchCashReportItemsStatus = fetchStatuses.success;
    });
    builder.addCase(fetchCashReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;

      state.fetchCashReportItemsStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchCashTransactionsReport.pending, (state) => {
      state.error = null;
      state.fetchCashTransactionsReportStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchCashTransactionsReport.fulfilled, (state, action) => {
      state.cashTransactionsItems = action.payload.data.items;
      state.totalCashTransactionsItems = Number(action.payload.data.totalItems) || 0;
      state.fetchCashTransactionsReportStatus = fetchStatuses.success;
    });
    builder.addCase(fetchCashTransactionsReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;

      state.fetchCashTransactionsReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchUsersReport.pending, (state) => {
      state.error = null;
      state.fetchUsersReportStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchUsersReport.fulfilled, (state, action) => {
      state.usersReportItems = action.payload.data.items;
      state.totalUsersReportItems = Number(action.payload.data.totalItems) || 0;
      state.fetchUsersReportStatus = fetchStatuses.success;
    });
    builder.addCase(fetchUsersReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchUsersReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchOrdersReport.pending, (state) => {
      state.error = null;
      state.fetchOrdersReportStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchOrdersReport.fulfilled, (state, action) => {
      state.ordersReportItems = action.payload.data.items;
      state.totalOrdersReportItems = Number(action.payload.data.totalItems) || 0;
      state.fetchOrdersReportStatus = fetchStatuses.success;
    });
    builder.addCase(fetchOrdersReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchOrdersReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchAumReport.pending, (state) => {
      state.error = null;
      state.fetchAumReportStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchAumReport.fulfilled, (state, action: PayloadAction<FetchAumReportResponse>) => {
      state.aumReportItems = action.payload.data.items;
      state.totalAumReportItems = Number(action.payload.data.totalItems) || 0;
      state.fetchAumReportStatus = fetchStatuses.success;
    });
    builder.addCase(fetchAumReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchAumReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchAccumulatedAumReport.pending, (state) => {
      state.error = null;
      state.fetchAccumulatedAumReportStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchAccumulatedAumReport.fulfilled,
      (state, action: PayloadAction<FetchAccumulatedAumReportResponse>) => {
        state.accumulatedAumReportItems = action.payload.data.items;
        state.totalAccumulatedAumReportItems = Number(action.payload.data.totalItems) || 0;
        state.fetchAccumulatedAumReportStatus = fetchStatuses.success;
      },
    );
    builder.addCase(fetchAccumulatedAumReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchAccumulatedAumReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchPortfolioDetailsReport.pending, (state) => {
      state.error = null;
      state.fetchPortfolioDetailsReportStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchPortfolioDetailsReport.fulfilled, (state, action) => {
      state.portfolioDetailsReportItems = action.payload.data.items;
      state.totalPortfolioDetailsReportItems = Number(action.payload.data.totalItems) || 0;
      state.fetchPortfolioDetailsReportStatus = fetchStatuses.success;
    });
    builder.addCase(fetchPortfolioDetailsReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchPortfolioDetailsReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchPortfolioTotalsReport.pending, (state) => {
      state.error = null;
      state.fetchPortfolioTotalsReportStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchPortfolioTotalsReport.fulfilled, (state, action) => {
      state.portfolioTotalsReportItems = action.payload.data.items;
      state.totalPortfolioTotalsReportItems = Number(action.payload.data.totalItems) || 0;
      state.fetchPortfolioTotalsReportStatus = fetchStatuses.success;
    });
    builder.addCase(fetchPortfolioTotalsReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchPortfolioTotalsReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchSystemCashReport.pending, (state) => {
      state.error = null;
      state.fetchSystemCashReportStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchSystemCashReport.fulfilled, (state, action: PayloadAction<FetchSystemCashReportResponse>) => {
      if (action.payload.data) {
        state.systemReport[action.payload.data.date] = action.payload.data;
      }
      state.fetchSystemCashReportStatus = fetchStatuses.success;
    });
    builder.addCase(fetchSystemCashReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;

      state.fetchSystemCashReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(reconcileSystemCashReport.pending, (state) => {
      state.error = null;
      state.reconcileSystemCashReportStatus = fetchStatuses.pending;
    });
    builder.addCase(reconcileSystemCashReport.fulfilled, (state) => {
      state.reconcileSystemCashReportStatus = fetchStatuses.success;
    });
    builder.addCase(reconcileSystemCashReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;

      state.reconcileSystemCashReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchUnitsReport.pending, (state) => {
      state.error = null;
      state.fetchUnitsReportItemsStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchUnitsReport.fulfilled, (state, action: PayloadAction<FetchUnitReportResponse>) => {
      state.unitItems = action.payload.data.items;
      state.totalUnitItems = action.payload.data.totalItems;

      state.fetchUnitsReportItemsStatus = fetchStatuses.success;
    });
    builder.addCase(fetchUnitsReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;

      state.fetchUnitsReportItemsStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchSystemUnitsReport.pending, (state) => {
      state.error = null;
      state.fetchSystemUnitsReportStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchSystemUnitsReport.fulfilled,
      (state, action: PayloadAction<FetchSystemUnitsReportResponse>) => {
        state.systemUnitsReport = action.payload.data;
        state.fetchSystemUnitsReportStatus = fetchStatuses.success;
      },
    );
    builder.addCase(fetchSystemUnitsReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;

      state.fetchSystemUnitsReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchFxRevenueAlpacaOrdersReport.pending, (state) => {
      state.error = null;
      state.fetchAlpacaFxRevenueOrdersStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchFxRevenueAlpacaOrdersReport.fulfilled,
      (state, action: PayloadAction<FetchOrderFxRevenueReportResponse>) => {
        state.alpacaFxRevenueOrders = action.payload.data.items;
        state.totalAlpacaFxRevenueOrders = action.payload.data.totalItems;
        state.fetchAlpacaFxRevenueOrdersStatus = fetchStatuses.success;
      },
    );
    builder.addCase(fetchFxRevenueAlpacaOrdersReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchAlpacaFxRevenueOrdersStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchFxRevenueAlpacaTransfersReport.pending, (state) => {
      state.error = null;
      state.fetchAlpacaFxRevenueTransferStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchFxRevenueAlpacaTransfersReport.fulfilled,
      (state, action: PayloadAction<FetchTransferFxRevenueReportResponse>) => {
        state.alpacaFxRevenueTransfers = action.payload.data.items;
        state.totalAlpacaFxRevenueTransfers = action.payload.data.totalItems;
        state.fetchAlpacaFxRevenueTransferStatus = fetchStatuses.success;
      },
    );
    builder.addCase(fetchFxRevenueAlpacaTransfersReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchAlpacaFxRevenueTransferStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchFxRevenueJadwaOrdersReport.pending, (state) => {
      state.error = null;
      state.fetchJadwaFxRevenueOrdersStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchFxRevenueJadwaOrdersReport.fulfilled,
      (state, action: PayloadAction<FetchOrderFxRevenueReportResponse>) => {
        state.jadwaFxRevenueOrders = action.payload.data.items;
        state.totalJadwaFxRevenueOrders = action.payload.data.totalItems;
        state.fetchJadwaFxRevenueOrdersStatus = fetchStatuses.success;
      },
    );
    builder.addCase(fetchFxRevenueJadwaOrdersReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchJadwaFxRevenueOrdersStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchFxRevenueJadwaTransfersReport.pending, (state) => {
      state.error = null;
      state.fetchJadwaFxRevenueTransferStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchFxRevenueJadwaTransfersReport.fulfilled,
      (state, action: PayloadAction<FetchTransferFxRevenueReportResponse>) => {
        state.jadwaFxRevenueTransfers = action.payload.data.items;
        state.totalJadwaFxRevenueTransfers = action.payload.data.totalItems;
        state.fetchJadwaFxRevenueTransferStatus = fetchStatuses.success;
      },
    );
    builder.addCase(fetchFxRevenueJadwaTransfersReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchJadwaFxRevenueTransferStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchUserIndexFundReport.pending, (state) => {
      state.error = null;
      state.fetchUserIndexFundReportStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchUserIndexFundReport.fulfilled,
      (state, action: PayloadAction<FetchUserIndexFundReportResponse>) => {
        state.userIndexFundReport = action.payload.data;
        state.totalUserIndexFundReport = action.payload.data.length;
        state.fetchUserIndexFundReportStatus = fetchStatuses.success;
      },
    );
    builder.addCase(fetchUserIndexFundReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchUserIndexFundReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchAlpacaJournalReport.pending, (state) => {
      state.error = null;
      state.fetchAlpacaJournalReportStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchAlpacaJournalReport.fulfilled, (state, action: PayloadAction<FetchAlpacaJournalResponse>) => {
      state.alpacaJournalReport = action.payload.data;
      state.totalAlpacaJournalReport = action.payload.data.length;
      state.fetchAlpacaJournalReportStatus = fetchStatuses.success;
    });
    builder.addCase(fetchAlpacaJournalReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchAlpacaJournalReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    // CMA Users Report
    builder.addCase(fetchCmaUsersReport.pending, (state) => {
      state.error = null;
      state.fetchCmaUsersReportStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchCmaUsersReport.fulfilled, (state, action: PayloadAction<FetchCmaUsersReportResponse>) => {
      state.cmaUsersItems = action.payload.data.items;
      state.totalCmaUsersItems = Number(action.payload.data.totalItems) || 0;
      state.fetchCmaUsersReportStatus = fetchStatuses.success;
    });
    builder.addCase(fetchCmaUsersReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchCmaUsersReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchUserDetailsReport.pending, (state) => {
      state.error = null;
      state.fetchUserDetailsReportStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchUserDetailsReport.fulfilled,
      (state, action: PayloadAction<FetchUserDetailsReportResponse>) => {
        state.userDetailsReportItems = action.payload.data.items;
        state.totalUserDetailsReportItems = Number(action.payload.data.totalItems) || 0;
        state.fetchUserDetailsReportStatus = fetchStatuses.success;
      },
    );
    builder.addCase(fetchUserDetailsReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchUserDetailsReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchFeeCalculationsReport.pending, (state) => {
      state.error = null;
      state.fetchFeeCalculationsReportStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchFeeCalculationsReport.fulfilled,
      (state, action: PayloadAction<FetchFeeCalculationsReportResponse>) => {
        state.feeCalculationsReportItems = action.payload.data.items;
        state.totalFeeCalculationsReportItems = Number(action.payload.data.totalItems) || 0;
        state.fetchFeeCalculationsReportStatus = fetchStatuses.success;
      },
    );
    builder.addCase(fetchFeeCalculationsReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchFeeCalculationsReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchFinanceUsersReport.pending, (state) => {
      state.error = null;
      state.fetchFinanceUsersReportStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchFinanceUsersReport.fulfilled,
      (state, action: PayloadAction<FetchFinanceUsersReportResponse>) => {
        state.financeUsersReportItems = action.payload.data.items;
        state.totalFinanceUsersReportItems = Number(action.payload.data.totalItems) || 0;
        state.fetchFinanceUsersReportStatus = fetchStatuses.success;
      },
    );
    builder.addCase(fetchFinanceUsersReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchFinanceUsersReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchPortfolioCompositionReport.pending, (state) => {
      state.error = null;
      state.fetchPortfolioCompositionReportStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchPortfolioCompositionReport.fulfilled,
      (state, action: PayloadAction<FetchPortfolioCompositionReportResponse>) => {
        state.portfolioCompositionReportItems = action.payload.data.items;
        state.totalPortfolioCompositionReportItems = Number(action.payload.data.totalItems) || 0;
        state.fetchPortfolioCompositionReportStatus = fetchStatuses.success;
      },
    );
    builder.addCase(fetchPortfolioCompositionReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchPortfolioCompositionReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchPortfolioSnapshotReport.pending, (state) => {
      state.error = null;
      state.fetchPortfolioSnapshotReportStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchPortfolioSnapshotReport.fulfilled,
      (state, action: PayloadAction<FetchPortfolioSnapshotReportResponse>) => {
        state.portfolioSnapshotReportItems = action.payload.data.items;
        state.totalPortfolioSnapshotReportItems = Number(action.payload.data.totalItems) || 0;
        state.fetchPortfolioSnapshotReportStatus = fetchStatuses.success;
      },
    );
    builder.addCase(fetchPortfolioSnapshotReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchPortfolioSnapshotReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(fetchPortfolioOrdersDataReport.pending, (state) => {
      state.error = null;
      state.fetchPortfolioOrdersDataReportStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchPortfolioOrdersDataReport.fulfilled,
      (state, action: PayloadAction<FetchPortfolioOrdersDataReportResponse>) => {
        state.portfolioOrdersDataReportItems = action.payload.data.items;
        state.totalPortfolioOrdersDataReportItems = Number(action.payload.data.totalItems) || 0;
        state.fetchPortfolioOrdersDataReportStatus = fetchStatuses.success;
      },
    );
    builder.addCase(fetchPortfolioOrdersDataReport.rejected, (state, action) => {
      const payload = (action.payload || {}) as IAnyObject;
      state.fetchPortfolioOrdersDataReportStatus = fetchStatuses.rejected;
      state.error = { ...action.error, ...payload };
    });

    builder.addCase(cleanReport.type, (state) => {
      Object.assign(state, initialState);
    });
  },
});

export const reportReducer = portfolioOrdersSlice.reducer;
